import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import io, { Socket } from 'socket.io-client';
import Layout from '../components/layout/Layout';

interface User {
  id: number;
  name: string;
  email: string;
  account_number: string;
  is_verified: boolean;
  profile_picture: string | null;
}

interface OtherUser {
  id: number;
  name: string;
  email: string;
  account_number: string;
  profile_picture: string | null;
}

interface Conversation {
  id: number;
  user1_id: number;
  user2_id: number;
  last_message_at: string;
  last_message_text: string;
  last_message_type: string;
  last_message_sender_id: number;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  other_user: OtherUser;
}

interface MessageEditHistoryItem {
  id: number;
  original_text: string;
  edited_text: string;
  edit_reason: string | null;
  edited_at: string;
}

interface Message {
  id: number;
  sender_id: number;
  receiver_id: number;
  message_type: string;
  text_content: string | null;
  user_gallery_url: string | null;
  user_library_url: string | null;
  voice_note_url: string | null;
  location_data: {
    latitude: number;
    longitude: number;
    address: string;
    location_name: string;
  } | null;
  user_gallery_data?: {
    media_url: string;
    media_type: 'image' | 'video';
    thumbnail_url: string | null;
    is_deleted: boolean;
  };
  user_library_data?: {
    library_id: number;
    name: string;
    cover_url: string | null;
    media_count: number;
    is_deleted: boolean;
  };
  is_deleted: boolean;
  is_unsent: boolean;
  created_at: string;
  updated_at: string;
  read_at: string | null;
  edit_history: MessageEditHistoryItem[];
}

interface LibraryMediaItem {
  id: number;
  media_url: string;
  media_type: 'image' | 'video';
  thumbnail_url: string | null;
  is_deleted: boolean;
  created_at: string;
}

const Chat: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  
  // Lightbox and modal states
  const [showLightbox, setShowLightbox] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<string | null>(null);
  const [selectedMediaType, setSelectedMediaType] = useState<'image' | 'video'>('image');
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [selectedLibrary, setSelectedLibrary] = useState<any>(null);
  const [libraryMediaItems, setLibraryMediaItems] = useState<LibraryMediaItem[]>([]);
  const [loadingLibraryMedia, setLoadingLibraryMedia] = useState(false);

  // Fetch user details
  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      setLoading(true);
      try {
        const response = await axios.get(`/admin/users/${userId}`);
        if (response.data.success) {
          setUser(response.data.data.user);
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  // Fetch user conversations
  const fetchUserConversations = async (currentUserId: number) => {
    setConversationLoading(true);
    try {
      console.log('Fetching conversations for user:', currentUserId);
      const response = await axios.get(`/admin/users/${currentUserId}/conversations`);
      console.log('Conversations response:', response.data);
      if (response.data.success) {
        setConversations(response.data.data.conversations);
        console.log('Set conversations:', response.data.data.conversations);
      } else {
        console.error('API returned success: false', response.data);
      }
    } catch (error) {
      console.error('Error fetching user conversations:', error);
    } finally {
      setConversationLoading(false);
    }
  };

  // Fetch conversation messages with pagination
  const fetchConversationMessages = async (currentUserId: number, conversationId: number, page: number = 1, append: boolean = false) => {
    if (append) {
      setLoadingMoreMessages(true);
    } else {
      setMessagesLoading(true);
    }
    
    try {
      const response = await axios.get(`/admin/users/${currentUserId}/conversations/${conversationId}/messages?page=${page}&limit=20`);
      if (response.data.success) {
        const newMessages = response.data.data.messages;
        const pagination = response.data.data.pagination;
        
        if (append) {
          setMessages(prev => [...newMessages, ...prev]);
        } else {
          setMessages(newMessages);
        }
        
        setCurrentPage(page);
        setHasMoreMessages(page < pagination.total_pages);
      }
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
    } finally {
      if (append) {
        setLoadingMoreMessages(false);
      } else {
        setMessagesLoading(false);
      }
    }
  };

  // Load more messages (pagination)
  const loadMoreMessages = async () => {
    if (selectedConversation && user && hasMoreMessages && !loadingMoreMessages) {
      await fetchConversationMessages(user.id, selectedConversation.id, currentPage + 1, true);
    }
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Handle scroll to load more messages
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    if (scrollTop === 0 && hasMoreMessages && !loadingMoreMessages) {
      loadMoreMessages();
    }
  };

  // Open lightbox for gallery media
  const openLightbox = (mediaUrl: string, mediaType: 'image' | 'video' = 'image') => {
    setSelectedMedia(mediaUrl);
    setSelectedMediaType(mediaType);
    setShowLightbox(true);
  };

  // Close lightbox
  const closeLightbox = () => {
    setShowLightbox(false);
    setSelectedMedia(null);
    setSelectedMediaType('image');
  };

  // Open library modal and fetch media
  const openLibraryModal = async (libraryData: any) => {
    try {
      setSelectedLibrary(libraryData);
      setShowLibraryModal(true);
      setLoadingLibraryMedia(true);
      
      const response = await axios.get(`/admin/libraries/${libraryData.library_id}/media`);
      if (response.data.success) {
        setLibraryMediaItems(response.data.data.media);
      }
    } catch (error) {
      console.error('Error fetching library media:', error);
    } finally {
      setLoadingLibraryMedia(false);
    }
  };

  // Close library modal
  const closeLibraryModal = () => {
    setShowLibraryModal(false);
    setSelectedLibrary(null);
    setLibraryMediaItems([]);
  };

  // Open media from library in lightbox
  const openLibraryMediaInLightbox = (media: LibraryMediaItem) => {
    closeLibraryModal();
    openLightbox(media.media_url, media.media_type as 'image' | 'video');
  };

  // Fetch conversations when user is loaded
  useEffect(() => {
    if (user) {
      fetchUserConversations(user.id);
    }
  }, [user]);

  // Fetch messages when a conversation is selected
  useEffect(() => {
    if (selectedConversation && user) {
      setCurrentPage(1);
      setHasMoreMessages(true);
      setMessages([]);
      fetchConversationMessages(user.id, selectedConversation.id, 1, false);
      
      // Join conversation room via socket
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('join_conversation', { conversationId: selectedConversation.id });
      }
    }
  }, [selectedConversation, user]);

  // Scroll to bottom when messages are loaded (initial load only)
  useEffect(() => {
    if (messages.length > 0 && !loadingMoreMessages && currentPage === 1) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages.length, loadingMoreMessages, currentPage]);

  // Initialize socket connection
  useEffect(() => {
    // Connect to socket.io server
    const socket = io('/messages', {
      transports: ['websocket', 'polling']
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket connected');
    });

    socket.on('connected', (data: any) => {
      console.log('Socket authenticated:', data);
    });

    // Listen for new messages
    socket.on('new_message', (data: any) => {
      if (data.success && data.message) {
        setMessages(prev => [...prev, data.message]);
        setTimeout(scrollToBottom, 100);
        
        // Update conversation list
        if (user) {
          fetchUserConversations(user.id);
        }
      }
    });

    // Listen for message edits
    socket.on('message_edited', (data: any) => {
      if (data.success && data.messageId) {
        setMessages(prev => prev.map(msg => 
          msg.id === data.messageId 
            ? { ...msg, text_content: data.newText, updated_at: data.editedAt } 
            : msg
        ));
      }
    });

    // Listen for message unsends
    socket.on('message_unsent', (data: any) => {
      if (data.success && data.messageId) {
        setMessages(prev => prev.map(msg => 
          msg.id === data.messageId 
            ? { ...msg, is_unsent: true } 
            : msg
        ));
      }
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    socket.on('error', (error: any) => {
      console.error('Socket error:', error);
    });

    return () => {
      if (socketRef.current) {
        if (selectedConversation) {
          socketRef.current.emit('leave_conversation', { conversationId: selectedConversation.id });
        }
        socketRef.current.disconnect();
      }
    };
  }, [user, selectedConversation]);

  if (loading) {
    return (
      <Layout>
        <div className="page-content">
          <div className="container-fluid">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="page-content">
          <div className="container-fluid">
            <div className="text-center py-5">
              <h5>User not found</h5>
              <button className="btn btn-primary" onClick={() => navigate('/users')}>
                Back to Users
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content">
        <div className="container-fluid">
          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">Chat - {user.name}</h4>
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    <li className="breadcrumb-item">
                      <button className="btn btn-link p-0" onClick={() => navigate('/users')}>
                        Users
                      </button>
                    </li>
                    <li className="breadcrumb-item active">Chat</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Chat Interface */}
          <div className="d-lg-flex">
            {/* Left Sidebar - Conversations */}
            <div className="chat-leftsidebar me-lg-4">
              <div className="card shadow-sm border-0">
                {/* User Info Header */}
                <div className="py-4 px-3 border-bottom" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 me-3 position-relative">
                      {user.profile_picture ? (
                        <img 
                          src={user.profile_picture} 
                          className="rounded-circle border border-3 border-white shadow-sm" 
                          style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          alt="User"
                          onError={(e) => {
                            e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=fff&color=667eea&size=48&bold=true`;
                          }}
                        />
                      ) : (
                        <img 
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=fff&color=667eea&size=48&bold=true`}
                          className="rounded-circle border border-3 border-white shadow-sm" 
                          style={{ width: '48px', height: '48px' }}
                          alt="User"
                        />
                      )}
                      <span 
                        className={`position-absolute bottom-0 end-0 p-1 ${user.is_verified ? 'bg-success' : 'bg-warning'} border border-2 border-white rounded-circle`}
                        style={{ width: '14px', height: '14px' }}
                      ></span>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="mb-1 text-white font-weight-bold">{user.name}</h5>
                      <p className="mb-0 text-white-50 small">
                        <i className="mdi mdi-shield-check me-1"></i>
                        {user.is_verified ? 'Verified Account' : 'Not Verified'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Conversations List */}
                <div className="chat-leftsidebar-nav">
                  <div className="py-3">
                    <div className="px-3 mb-3 d-flex align-items-center justify-content-between">
                      <h5 className="font-size-14 mb-0 fw-bold text-dark">
                        <i className="bx bx-message-rounded-dots me-2 text-primary"></i>
                        Conversations
                      </h5>
                      <span className="badge bg-primary rounded-pill">{conversations.length}</span>
                    </div>
                    {conversationLoading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted mt-2 small">Loading conversations...</p>
                      </div>
                    ) : conversations.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="mb-3">
                          <i className="bx bx-message-dots text-muted" style={{ fontSize: '48px', opacity: 0.5 }}></i>
                        </div>
                        <h6 className="text-muted">No conversations yet</h6>
                        <p className="text-muted small">Conversations will appear here</p>
                      </div>
                    ) : (
                      <ul className="list-unstyled chat-list mb-0" style={{ maxHeight: '500px', overflowY: 'auto', scrollbarWidth: 'thin' }}>
                        {conversations.map((conversation) => {
                          const isSelected = selectedConversation?.id === conversation.id;
                          return (
                            <li 
                              key={conversation.id}
                              className="px-2 mb-1"
                            >
                              <div
                                onClick={() => setSelectedConversation(conversation)}
                                className={`d-flex align-items-center p-3 rounded-3 cursor-pointer transition-all ${
                                  isSelected 
                                    ? 'text-white' 
                                    : 'hover-bg-light'
                                }`}
                                style={{ 
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  backgroundColor: isSelected ? '#006175' : 'transparent',
                                  borderLeft: isSelected ? '3px solid #006175' : '3px solid transparent'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                              >
                                <div className="flex-shrink-0 me-3 position-relative">
                                  {conversation.other_user.profile_picture ? (
                                    <img 
                                      src={conversation.other_user.profile_picture} 
                                      className="rounded-circle shadow-sm" 
                                      style={{ width: '42px', height: '42px', objectFit: 'cover' }}
                                      alt="Profile"
                                      onError={(e) => {
                                        e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.other_user.name)}&background=667eea&color=fff&size=42&bold=true`;
                                      }}
                                    />
                                  ) : (
                                    <img 
                                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.other_user.name)}&background=667eea&color=fff&size=42&bold=true`}
                                      className="rounded-circle shadow-sm" 
                                      style={{ width: '42px', height: '42px' }}
                                      alt="Profile"
                                    />
                                  )}
                                  {conversation.is_active && (
                                    <span 
                                      className="position-absolute bottom-0 end-0 bg-success border border-2 border-white rounded-circle"
                                      style={{ width: '12px', height: '12px' }}
                                    ></span>
                                  )}
                                  {conversation.is_deleted && (
                                    <span 
                                      className="position-absolute top-0 end-0 bg-danger border border-2 border-white rounded-circle"
                                      style={{ width: '12px', height: '12px' }}
                                      title="Deleted conversation"
                                    ></span>
                                  )}
                                </div>
                                <div className="flex-grow-1 overflow-hidden">
                                  <div className="d-flex justify-content-between align-items-start mb-1">
                                    <h6 className={`text-truncate mb-0 fw-semibold ${isSelected ? 'text-white' : 'text-dark'}`} style={{ fontSize: '14px' }}>
                                      {conversation.other_user.name}
                                    </h6>
                                    <small className={isSelected ? 'text-white-50' : 'text-muted'} style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>
                                      {conversation.last_message_at ? 
                                        new Date(conversation.last_message_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 
                                        ''
                                      }
                                    </small>
                                  </div>
                                  <p className={`text-truncate mb-0 small ${isSelected ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '12px' }}>
                                    {conversation.last_message_sender_id === user.id && (
                                      <i className={`bx bx-check-double me-1 ${isSelected ? 'text-white' : 'text-primary'}`}></i>
                                    )}
                                    {conversation.last_message_text || 'No messages'}
                                  </p>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Chat Area */}
            <div className="w-100 user-chat">
              <div className="card shadow-sm border-0">
                {selectedConversation ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-bottom bg-white">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0 me-3 position-relative">
                            {selectedConversation.other_user.profile_picture ? (
                              <img 
                                src={selectedConversation.other_user.profile_picture} 
                                className="rounded-circle shadow-sm" 
                                style={{ width: '46px', height: '46px', objectFit: 'cover' }}
                                alt="Profile"
                                onError={(e) => {
                                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedConversation.other_user.name)}&background=667eea&color=fff&size=46&bold=true`;
                                }}
                              />
                            ) : (
                              <img 
                                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(selectedConversation.other_user.name)}&background=667eea&color=fff&size=46&bold=true`}
                                className="rounded-circle shadow-sm" 
                                style={{ width: '46px', height: '46px' }}
                                alt="Profile"
                              />
                            )}
                            {selectedConversation.is_active && (
                              <span 
                                className="position-absolute bottom-0 end-0 bg-success border border-2 border-white rounded-circle"
                                style={{ width: '13px', height: '13px' }}
                              ></span>
                            )}
                          </div>
                          <div>
                            <h5 className="mb-1 fw-bold">{selectedConversation.other_user.name}</h5>
                            <p className="mb-0 text-muted small">
                              <i className="bx bx-user-circle me-1"></i>
                              Account #{selectedConversation.other_user.account_number}
                              {selectedConversation.is_deleted && (
                                <span className="badge bg-danger ms-2 rounded-pill" style={{ fontSize: '9px' }}>Deleted</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div>
                          <ul className="list-inline mb-0 d-flex align-items-center gap-2">
                            <li className="list-inline-item">
                              <button 
                                className="btn btn-light btn-sm rounded-circle shadow-sm" 
                                style={{ width: '38px', height: '38px' }}
                                title="Search messages"
                              >
                                <i className="bx bx-search-alt-2"></i>
                              </button>
                            </li>
                            <li className="list-inline-item">
                              <div className="dropdown">
                                <button 
                                  className="btn btn-light btn-sm rounded-circle shadow-sm" 
                                  style={{ width: '38px', height: '38px' }}
                                  type="button" 
                                  data-bs-toggle="dropdown" 
                                  aria-haspopup="true" 
                                  aria-expanded="false"
                                  title="More options"
                                >
                                  <i className="bx bx-dots-vertical-rounded"></i>
                                </button>
                                <div className="dropdown-menu dropdown-menu-end shadow">
                                  <button type="button" className="dropdown-item">
                                    <i className="bx bx-user me-2"></i>View Profile
                                  </button>
                                  <button type="button" className="dropdown-item">
                                    <i className="bx bx-trash me-2"></i>Clear chat
                                  </button>
                                  <button type="button" className="dropdown-item">
                                    <i className="bx bx-bell-off me-2"></i>Mute
                                  </button>
                                  <div className="dropdown-divider"></div>
                                  <button type="button" className="dropdown-item text-danger">
                                    <i className="bx bx-block me-2"></i>Block User
                                  </button>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Messages Area */}
                    <div>
                      <div className="chat-conversation p-4" style={{ background: '#f8f9fa' }}>
                        {messagesLoading ? (
                          <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted mt-3 small">Loading messages...</p>
                          </div>
                        ) : messages.length === 0 ? (
                          <div className="text-center py-5">
                            <div className="mb-3">
                              <i className="bx bx-message-dots text-muted" style={{ fontSize: '64px', opacity: 0.4 }}></i>
                            </div>
                            <h5 className="text-muted">No messages yet</h5>
                            <p className="text-muted small">Start a conversation with {selectedConversation.other_user.name}</p>
                          </div>
                        ) : (
                          <div 
                            ref={messagesContainerRef}
                            style={{ maxHeight: '486px', overflowY: 'auto' }}
                            onScroll={handleScroll}
                          >
                            <ul className="list-unstyled mb-0">
                              {/* Load More Messages Indicator */}
                              {loadingMoreMessages && (
                                <li className="text-center py-2">
                                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  <small className="text-muted ms-2">Loading more messages...</small>
                                </li>
                              )}

                              {/* Messages */}
                              {messages.map((message) => {
                                const isSender = message.sender_id === user.id;
                                return (
                                <li key={message.id} className={`mb-3 ${isSender ? 'd-flex justify-content-end' : ''}`}>
                                  <div className={`d-flex ${isSender ? 'flex-row-reverse' : ''}`} style={{ maxWidth: '70%' }}>
                                    {!isSender && (
                                      <div className="flex-shrink-0 me-2">
                                        <img 
                                          src={selectedConversation.other_user.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedConversation.other_user.name)}&background=667eea&color=fff&size=32&bold=true`}
                                          className="rounded-circle shadow-sm" 
                                          style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                          alt="Profile"
                                        />
                                      </div>
                                    )}
                                    <div className={`flex-grow-1 ${isSender ? 'text-end' : ''}`}>
                                        <div 
                                        className={`p-3 rounded-3 shadow-sm position-relative ${
                                          isSender 
                                            ? 'bg-primary text-white' 
                                            : 'bg-white'
                                        }`}
                                        style={{
                                          borderTopLeftRadius: !isSender ? '4px !important' : undefined,
                                          borderTopRightRadius: isSender ? '4px !important' : undefined
                                        }}
                                      >
                                        {/* Message options removed */}
                                        
                                        <div className="conversation-name fw-semibold mb-2 small" style={{ opacity: 0.8 }}>
                                          {isSender ? user.name : selectedConversation.other_user.name}
                                        </div>
                                        
                                        {/* Status Badges */}
                                        {(message.is_deleted || message.is_unsent || message.message_type !== 'text') && (
                                          <div className="mb-2 d-flex flex-wrap gap-1">
                                            {message.message_type !== 'text' && (
                                              <span className={`badge ${isSender ? 'bg-white text-dark border' : 'bg-primary text-white'} rounded-pill`} style={{ fontSize: '9px' }}>
                                                <i className={`bx ${
                                                  message.message_type === 'user_gallery' ? 'bx-image' :
                                                  message.message_type === 'user_library' ? 'bx-folder' :
                                                  message.message_type === 'voice_note' ? 'bx-microphone' :
                                                  message.message_type === 'location' ? 'bx-map' :
                                                  'bx-message'
                                                } me-1`}></i>
                                                {message.message_type}
                                              </span>
                                            )}
                                            {message.is_deleted && (
                                              <span className="badge bg-danger rounded-pill" style={{ fontSize: '9px' }}>
                                                <i className="bx bx-trash me-1"></i>Deleted
                                              </span>
                                            )}
                                            {message.is_unsent && (
                                              <span className="badge bg-warning rounded-pill" style={{ fontSize: '9px' }}>
                                                <i className="bx bx-x-circle me-1"></i>Unsent
                                              </span>
                                            )}
                                          </div>
                                        )}

                                        {/* Message Content */}
                                        {message.is_unsent ? (
                                          <p className={`mb-0 small fst-italic ${isSender ? 'text-white-50' : 'text-muted'}`}>
                                            <i className="bx bx-block me-1"></i>
                                            This message was unsent
                                          </p>
                                        ) : (
                                          <>
                                            {/* Text Content */}
                                            {message.text_content && (
                                              <p className="mb-0">{message.text_content}</p>
                                            )}

                                            {/* User Gallery Content */}
                                            {message.user_gallery_data && (
                                              <div className="mt-2">
                                                {message.user_gallery_data.media_type === 'video' ? (
                                                  <div 
                                                    className="position-relative d-inline-block" 
                                                    onClick={() => openLightbox(message.user_gallery_data!.media_url, 'video')}
                                                    style={{ cursor: 'pointer' }}
                                                  >
                                                    <img 
                                                      src={message.user_gallery_data.thumbnail_url || message.user_gallery_data.media_url} 
                                                      alt="Video thumbnail" 
                                                      className="rounded border shadow-sm" 
                                                      style={{ maxWidth: '250px', maxHeight: '200px', objectFit: 'cover' }} 
                                                    />
                                                    <div 
                                                      className="position-absolute top-50 start-50 translate-middle"
                                                      style={{ 
                                                        width: '50px', 
                                                        height: '50px', 
                                                        backgroundColor: 'rgba(0,0,0,0.6)', 
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                      }}
                                                    >
                                                      <i className="bx bx-play text-white" style={{ fontSize: '28px' }}></i>
                                                    </div>
                                                    {message.user_gallery_data.is_deleted && (
                                                      <span className="position-absolute top-0 start-0 m-2 badge bg-danger rounded-pill" style={{ fontSize: '9px' }}>
                                                        Deleted
                                                      </span>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <div 
                                                    className="d-inline-block position-relative" 
                                                    onClick={() => openLightbox(message.user_gallery_data!.media_url, 'image')}
                                                    style={{ cursor: 'pointer' }}
                                                  >
                                                    <img 
                                                      src={message.user_gallery_data.media_url} 
                                                      alt="Gallery" 
                                                      className="rounded border shadow-sm" 
                                                      style={{ maxWidth: '250px' }} 
                                                    />
                                                    {message.user_gallery_data.is_deleted && (
                                                      <span className="position-absolute top-0 start-0 m-2 badge bg-danger rounded-pill" style={{ fontSize: '9px' }}>
                                                        Deleted
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            )}

                                            {/* User Library Content */}
                                            {message.user_library_data && (
                                              <div className="mt-2">
                                                <div 
                                                  className="d-inline-block position-relative" 
                                                  onClick={() => openLibraryModal(message.user_library_data!)}
                                                  style={{ cursor: 'pointer' }}
                                                >
                                                  {message.user_library_data.cover_url ? (
                                                    <div className="position-relative">
                                                      <img 
                                                        src={message.user_library_data.cover_url} 
                                                        alt={message.user_library_data.name} 
                                                        className="rounded border shadow-sm" 
                                                        style={{ width: '200px', height: '150px', objectFit: 'cover' }} 
                                                      />
                                                      <div 
                                                        className="position-absolute bottom-0 start-0 end-0 p-2"
                                                        style={{ 
                                                          background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                                                          borderBottomLeftRadius: '0.25rem',
                                                          borderBottomRightRadius: '0.25rem'
                                                        }}
                                                      >
                                                        <p className="mb-0 text-white fw-semibold small">{message.user_library_data.name}</p>
                                                        <p className="mb-0 text-white-50" style={{ fontSize: '11px' }}>
                                                          <i className="bx bx-image me-1"></i>
                                                          {message.user_library_data.media_count} item{message.user_library_data.media_count !== 1 ? 's' : ''}
                                                        </p>
                                                      </div>
                                                      {message.user_library_data.is_deleted && (
                                                        <span className="position-absolute top-0 start-0 m-2 badge bg-danger rounded-pill" style={{ fontSize: '9px' }}>
                                                          Deleted
                                                        </span>
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <div 
                                                      className="d-flex align-items-center justify-content-center rounded border shadow-sm"
                                                      style={{ width: '200px', height: '150px', backgroundColor: isSender ? 'rgba(255,255,255,0.1)' : '#f8f9fa' }}
                                                    >
                                                      <div className="text-center">
                                                        <i className="bx bx-folder" style={{ fontSize: '48px', color: isSender ? '#ffffff' : '#2C5F5D' }}></i>
                                                        <p className={`mb-0 fw-semibold small mt-2 ${isSender ? 'text-white' : 'text-dark'}`}>{message.user_library_data.name}</p>
                                                        <p className={`mb-0 ${isSender ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '11px' }}>
                                                          {message.user_library_data.media_count} item{message.user_library_data.media_count !== 1 ? 's' : ''}
                                                        </p>
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            )}

                                            {/* Location Content */}
                                            {message.location_data && (
                                              <div className="mt-2">
                                                <div className="d-inline-block">
                                                  <div className="mb-2">
                                                    <img 
                                                      src={`https://staticmap.openstreetmap.de/staticmap.php?center=${message.location_data.latitude},${message.location_data.longitude}&zoom=15&size=300x200&markers=${message.location_data.latitude},${message.location_data.longitude},red`}
                                                      alt="Location map" 
                                                      className="rounded border shadow-sm" 
                                                      style={{ maxWidth: '300px', width: '100%' }}
                                                      onError={(e) => {
                                                        e.currentTarget.style.display = 'none';
                                                      }}
                                                    />
                                                  </div>
                                                  <div className="d-flex align-items-start">
                                                    <i className={`bx bx-map me-2 ${isSender ? 'text-white' : 'text-danger'}`} style={{ fontSize: '20px' }}></i>
                                                    <div className="flex-grow-1">
                                                      {message.location_data.location_name && (
                                                        <p className={`mb-1 fw-semibold small ${isSender ? 'text-white' : 'text-dark'}`}>{message.location_data.location_name}</p>
                                                      )}
                                                      {message.location_data.address && (
                                                        <p className={`mb-2 small ${isSender ? 'text-white-50' : 'text-muted'}`}>{message.location_data.address}</p>
                                                      )}
                                                      <a 
                                                        href={`https://maps.google.com/?q=${message.location_data.latitude},${message.location_data.longitude}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={`btn btn-sm ${isSender ? 'btn-light text-dark' : 'btn-primary'}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ fontSize: '12px' }}
                                                      >
                                                        <i className="bx bx-map me-1"></i>
                                                        Open in Google Maps
                                                      </a>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            )}

                                            {/* Voice Note Content */}
                                            {message.voice_note_url && (
                                              <div className="mt-2">
                                                <audio controls className="w-100" style={{ maxWidth: '300px' }}>
                                                  <source src={message.voice_note_url} type="audio/mpeg" />
                                                  Your browser does not support the audio element.
                                                </audio>
                                              </div>
                                            )}

                                            {/* Edit History */}
                                            {message.edit_history && message.edit_history.length > 0 && (
                                              <div className={`mt-3 pt-2 ${isSender ? 'border-top border-white border-opacity-25' : 'border-top'}`}>
                                                <small className={isSender ? 'text-white-50' : 'text-muted'}>
                                                  <i className="bx bx-edit me-1"></i>
                                                  Edited {message.edit_history.length} time(s)
                                                </small>
                                                <div className="mt-2">
                                                  {message.edit_history.map((edit, index) => (
                                                    <div 
                                                      key={edit.id} 
                                                      className={`small p-2 rounded mb-2 ${
                                                        isSender 
                                                          ? 'bg-white bg-opacity-15 text-white-75' 
                                                          : 'bg-light text-muted'
                                                      }`}
                                                      style={{
                                                        backgroundColor: isSender ? 'rgba(255,255,255,0.15)' : '#f8f9fa',
                                                        color: isSender ? 'rgba(255,255,255,0.85)' : '#6c757d'
                                                      }}
                                                    >
                                                      <div className="mb-1"><strong>Edit {index + 1}:</strong> {new Date(edit.edited_at).toLocaleString()}</div>
                                                      <div><strong>Original:</strong> {edit.original_text}</div>
                                                      <div><strong>Edited:</strong> {edit.edited_text}</div>
                                                      {edit.edit_reason && <div className="mt-1"><strong>Reason:</strong> {edit.edit_reason}</div>}
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </>
                                        )}

                                        <div className={`mt-2 d-flex align-items-center gap-2 small ${isSender ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '11px' }}>
                                          <i className="bx bx-time-five"></i>
                                          <span>
                                            {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                          </span>
                                          {isSender && message.read_at && (
                                            <i className="bx bx-check-double text-success"></i>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Chat Input (Read-only) - Hidden for now */}
                      <div className="p-4 border-top bg-white" style={{ display: 'none' }}>
                        <div className="alert alert-info d-flex align-items-center mb-3 py-2 px-3 border-0" style={{ backgroundColor: '#e7f3ff' }}>
                          <i className="bx bx-info-circle me-2 text-primary" style={{ fontSize: '20px' }}></i>
                          <small className="mb-0 text-dark">
                            <strong>Read-only mode:</strong> Admin can view messages but cannot send new messages
                          </small>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <button className="btn btn-light rounded-circle shadow-sm" style={{ width: '42px', height: '42px' }} title="Emoji" disabled>
                            <i className="bx bx-smile"></i>
                          </button>
                          <button className="btn btn-light rounded-circle shadow-sm" style={{ width: '42px', height: '42px' }} title="Attach image" disabled>
                            <i className="bx bx-image"></i>
                          </button>
                          <button className="btn btn-light rounded-circle shadow-sm" style={{ width: '42px', height: '42px' }} title="Attach file" disabled>
                            <i className="bx bx-paperclip"></i>
                          </button>
                          <div className="flex-grow-1">
                            <input 
                              type="text" 
                              className="form-control border-0 shadow-sm" 
                              style={{ 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: '24px',
                                padding: '12px 20px'
                              }}
                              placeholder="Type a message..."
                              disabled
                            />
                          </div>
                          <button 
                            type="button" 
                            className="btn btn-primary rounded-circle shadow" 
                            style={{ 
                              width: '48px', 
                              height: '48px',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              border: 'none'
                            }} 
                            disabled
                            title="Send message"
                          >
                            <i className="bx bx-send" style={{ fontSize: '20px' }}></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '500px' }}>
                    <div className="text-center">
                      <div className="mb-4">
                        <div 
                          className="rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm"
                          style={{ 
                            width: '100px', 
                            height: '100px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          }}
                        >
                          <i className="bx bx-message-dots text-white" style={{ fontSize: '48px' }}></i>
                        </div>
                      </div>
                      <h4 className="mb-2">Select a Conversation</h4>
                      <p className="text-muted">Choose a conversation from the left sidebar to view messages</p>
                      
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox for Gallery Media */}
      {showLightbox && selectedMedia && (
        <div 
          className="modal fade show d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 9999 }}
          onClick={closeLightbox}
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content border-0 bg-transparent">
              <div className="modal-header border-0">
                <h5 className="modal-title text-white">
                  <i className={`bx ${selectedMediaType === 'video' ? 'bx-video' : 'bx-image'} me-2`}></i>
                  {selectedMediaType === 'video' ? 'Video' : 'Image'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={closeLightbox}
                ></button>
              </div>
              <div className="modal-body text-center p-0" onClick={(e) => e.stopPropagation()}>
                {selectedMediaType === 'video' ? (
                  <video 
                    controls 
                    autoPlay 
                    className="w-100" 
                    style={{ maxHeight: '80vh', objectFit: 'contain' }}
                  >
                    <source src={selectedMedia} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img 
                    src={selectedMedia} 
                    alt="Gallery" 
                    className="img-fluid"
                    style={{ maxHeight: '80vh', objectFit: 'contain' }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Library Modal */}
      {showLibraryModal && selectedLibrary && (
        <div 
          className="modal fade show d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
          onClick={closeLibraryModal}
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bx bx-folder me-2"></i>
                  {selectedLibrary.name}
                  {selectedLibrary.is_deleted && (
                    <span className="badge bg-danger ms-2 rounded-pill" style={{ fontSize: '10px' }}>
                      Deleted
                    </span>
                  )}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={closeLibraryModal}
                ></button>
              </div>
              <div className="modal-body">
                {loadingLibraryMedia ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-3 small">Loading library media...</p>
                  </div>
                ) : (
                  <div className="row">
                    {libraryMediaItems && libraryMediaItems.length > 0 ? (
                      libraryMediaItems.map((media, index) => (
                        <div key={media.id} className="col-md-3 col-sm-4 col-6 mb-3">
                          <div 
                            className="position-relative" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => openLibraryMediaInLightbox(media)}
                          >
                            <img 
                              src={media.thumbnail_url || media.media_url} 
                              alt={`Library ${index + 1}`}
                              className="img-fluid rounded shadow-sm"
                              style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                            />
                            {media.media_type === 'video' && (
                              <div 
                                className="position-absolute top-50 start-50 translate-middle"
                                style={{ 
                                  width: '40px', 
                                  height: '40px', 
                                  backgroundColor: 'rgba(0,0,0,0.6)', 
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <i className="bx bx-play text-white" style={{ fontSize: '24px' }}></i>
                              </div>
                            )}
                            <div className="position-absolute top-0 end-0 m-2">
                              <span className={`badge ${media.media_type === 'video' ? 'bg-danger' : 'bg-info'} rounded-pill`} style={{ fontSize: '10px' }}>
                                <i className={`bx ${media.media_type === 'video' ? 'bx-video' : 'bx-image'} me-1`}></i>
                                {media.media_type}
                              </span>
                            </div>
                            {media.is_deleted && (
                              <div className="position-absolute top-0 start-0 m-2">
                                <span className="badge bg-danger rounded-pill" style={{ fontSize: '10px' }}>
                                  <i className="bx bx-trash me-1"></i>Deleted
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-12 text-center py-5">
                        <i className="bx bx-folder-open text-muted mb-3" style={{ fontSize: '48px' }}></i>
                        <h6 className="text-muted">No media in this library</h6>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <div className="me-auto text-muted small">
                  <i className="bx bx-image me-1"></i>
                  {libraryMediaItems.length} item{libraryMediaItems.length !== 1 ? 's' : ''}
                </div>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closeLibraryModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Chat;

